import React, { useCallback, useEffect, useState } from 'react';
import { Inbox, Mail, RefreshCw, CheckCircle, DollarSign, Receipt } from 'lucide-react';
import { AdminApi } from '../../services/api';
import './QoreTalksAdmin.css';

const Header = ({ icon: Icon, title, subtitle, children }) => (
  <div className="qt-admin__header">
    <div>
      <h2><Icon size={22} /> {title}</h2>
      <p>{subtitle}</p>
    </div>
    {children && <div className="qt-admin__actions">{children}</div>}
  </div>
);

export const SupportTicketsPanel = ({ token, onNotify }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await AdminApi.getSupportTickets(token, { limit: 30 });
      setTickets(res.success ? (res.data.tickets || []) : []);
    } catch (e) {
      onNotify?.('error', 'Tickets', e.message);
    } finally {
      setLoading(false);
    }
  }, [token, onNotify]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    setBusy(id);
    try {
      const res = await AdminApi.resolveSupportTicket(token, id, 'Resolved from QoreNova Command');
      onNotify?.(res.success ? 'success' : 'error', 'Ticket', res.message || 'Updated');
      await load();
    } catch (e) {
      onNotify?.('error', 'Ticket', e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="qt-admin">
      <Header icon={Inbox} title="Support tickets" subtitle="Helper’s workplace — open issues across Academy, Talks, and Connect.">
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
      </Header>
      {loading ? <div className="loading-state"><RefreshCw className="spin" size={24} /></div> : (
        <div className="qt-subscribers-table">
          {tickets.map((t) => (
            <div key={t._id} className="qt-sub-row">
              <div className="qt-sub-avatar">{(t.priority || 'm')[0].toUpperCase()}</div>
              <div style={{ flex: 1 }}>
                <p className="qt-sub-email">{t.subject}</p>
                <p className="qt-sub-date">{t.ticketNumber} · {t.status} · {t.category} · {t.priority}</p>
              </div>
              {t.status !== 'resolved' && t.status !== 'closed' && (
                <button
                  type="button"
                  className="qt-btn qt-btn--primary qt-btn--sm"
                  disabled={busy === t._id}
                  onClick={() => resolve(t._id)}
                >
                  <CheckCircle size={14} /> Resolve
                </button>
              )}
            </div>
          ))}
          {tickets.length === 0 && <p className="empty-state">No tickets. Helper is idle.</p>}
        </div>
      )}
    </div>
  );
};

export const EmailTemplatesPanel = ({ token, onNotify }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await AdminApi.getEmailTemplates(token);
      setTemplates(res.success ? (res.data || []) : []);
    } catch (e) {
      onNotify?.('error', 'Templates', e.message);
    } finally {
      setLoading(false);
    }
  }, [token, onNotify]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="qt-admin">
      <Header icon={Mail} title="Email templates" subtitle="Atlas and Helper send from these templates.">
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
      </Header>
      {loading ? <div className="loading-state"><RefreshCw className="spin" size={24} /></div> : (
        <div className="qt-subscribers-table">
          {templates.map((t) => (
            <div key={t._id} className="qt-sub-row">
              <div className="qt-sub-avatar">@</div>
              <div>
                <p className="qt-sub-email">{t.name}</p>
                <p className="qt-sub-date">{t.slug} · {t.category} · {t.isActive ? 'active' : 'off'}</p>
              </div>
            </div>
          ))}
          {templates.length === 0 && <p className="empty-state">No templates yet.</p>}
        </div>
      )}
    </div>
  );
};

export const FinancePanel = ({ token, onNotify }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await AdminApi.getPaymentsDashboard(token);
      setData(res.success ? res.data : null);
    } catch (e) {
      onNotify?.('error', 'Finance', e.message);
    } finally {
      setLoading(false);
    }
  }, [token, onNotify]);

  useEffect(() => { load(); }, [load]);

  const ov = data?.overview || {};
  const txns = data?.recentPayments || [];

  return (
    <div className="qt-admin">
      <Header icon={DollarSign} title="Revenue" subtitle="Catalyst’s ledger — completed payments, failures, and refunds.">
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
      </Header>
      {loading ? <div className="loading-state"><RefreshCw className="spin" size={24} /></div> : (
        <>
          <div className="qt-stats">
            {[
              { label: 'Revenue', value: `$${(ov.totalRevenue || 0).toLocaleString()}`, color: '#34d399', icon: <DollarSign size={18} /> },
              { label: 'Transactions', value: ov.transactionCount || 0, color: '#6366f1', icon: <Receipt size={18} /> },
              { label: 'Avg ticket', value: `$${Math.round(ov.averageTransaction || 0).toLocaleString()}`, color: '#f59e0b', icon: <DollarSign size={18} /> },
              { label: 'Failed', value: ov.failedPayments || 0, color: '#fb7185', icon: <Receipt size={18} /> },
            ].map((s) => (
              <div key={s.label} className="qt-stat">
                <div className="qt-stat__icon" style={{ background: s.color }}>{s.icon}</div>
                <div>
                  <p className="qt-stat__value">{s.value}</p>
                  <p className="qt-stat__label">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="qt-subscribers-table">
            {txns.map((p) => (
              <div key={p._id} className="qt-sub-row">
                <div className="qt-sub-avatar">$</div>
                <div>
                  <p className="qt-sub-email">${p.totalAmount} · {p.status}</p>
                  <p className="qt-sub-date">{p.user?.email || p.user?.name} · {p.course?.title || p.gateway}</p>
                </div>
              </div>
            ))}
            {txns.length === 0 && <p className="empty-state">No payments recorded.</p>}
          </div>
        </>
      )}
    </div>
  );
};
